// app/src/features/reforecast/useReforecastWiring.js
import { useMemo } from "react";
import { useActiveForecast } from "../../hooks/useActiveForecast.js";
import { usePreviousVersion } from "../../hooks/usePreviousVersion.js";
import { useReforecastScrub } from "../../hooks/useReforecastScrub.js";
import { useSession } from "../../hooks/useSession.js";
import {
  buildReforecastEntries,
  isLiveScrubPosition,
} from "../../lib/forecast/reforecastEntries.js";
import { withLiveIdentity } from "../../lib/forecast/vintageContract.js";
import { selectResolutionSnapshot } from "../../lib/report/resolutionSlide.js";

/**
 * The reforecast wiring: the scrub list (`reforecastEntries`, one entry per forecast vintage,
 * the live sentinel and each resolution attempt), the snapshot scrub (`useReforecastScrub`, its
 * 3-field argument object passed unchanged), the position it derives (which resolution attempt is
 * selected, whether the position is the live sentinel, the snapshot the report's resolution slide
 * renders, the render-only overlay of the viewed snapshot onto `active`), the previous version a
 * scrubbed header compares with (`usePreviousVersion`) and where the probability line stops when
 * rewound. Called where `AppInner` built the scrub list: right after `active`, before the
 * alternate-space settings hooks (which read `viewSnap` / `viewEntry` from here).
 *
 * Not here: the resubmit (`useReforecast`, whose latest input is set after this point), the
 * mark-moment helpers (`useMarkMoments`, which read the timeline geometry) and the arrivals store
 * (C-23: `features/**` never imports the store). `forecastTracking` is the session context's
 * `_forecastTrackingAllowed`, read here through `useSession()`; `active` is read through
 * `useActiveForecast()`.
 */
export function useReforecastWiring() {
  const { _forecastTrackingAllowed: forecastTracking } = useSession();
  const active = useActiveForecast();
  // Clicking a reforecast mark on the date axis "rewinds" the displayed forecast to that snapshot:
  // triggers, final probability, report, and the contract drawer all reflect that moment while the
  // today line, zoom, and all the marks stay put. The rewind is render-only, and the selected
  // moment is deliberately EPHEMERAL: it is cleared whenever the active forecast changes, so
  // switching forecasts or reloading always defaults back to the most recent forecast. The gold
  // guide line and rewind click are driven off cursor proximity to a mark (see `activeMarkIso` in
  // useMarkMoments.js), so hovering anywhere in a mark's timeline column, not just the little hash,
  // surfaces the same line.
  const reforecastEntries = useMemo(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- the dependency array is BASE's (App.jsx), kept verbatim: `active` arrives through the hook's argument object and the compiler infers the whole object where the manual deps name its six fields; PKG-05 reconciles it
    () =>
      forecastTracking && active?.createdAt
        ? buildReforecastEntries(active.history, active.reportPointers, {
            createdAt: active.createdAt,
            lastReforecastedAt: active.lastReforecastedAt,
            // Resolution events are DISTINCT terminal entries at the tail (its own
            // scrub stop + verdict), NOT a verdict painted on the last forecast.
            resolution: active.resolution,
          })
        : [],
    [
      forecastTracking,
      active?.createdAt,
      active?.lastReforecastedAt,
      active?.history,
      active?.reportPointers,
      active?.resolution,
    ],
  );
  const {
    viewMomentIso,
    viewEntry,
    viewSnap,
    scrubbing: scrubLoading,
    scrubToMoment,
    returnToLive,
  } = useReforecastScrub({
    entries: reforecastEntries,
    activeId: active?.id,
    forecastTracking,
  });
  // The resolution entry at the CURRENT scrub position: the selected entry when
  // scrubbed to a resolution, else the tail (which on a resolved question is the
  // newest resolution) at rest. This is the single source of truth for WHICH
  // resolution attempt every surface renders — the verdict, the marker geometry,
  // and the report-modal slide — so scrubbing to an OLDER attempt shows THAT
  // attempt's status + moment, not the newest. `status` and `moment` ride on the
  // entry (from the resolution pointer), so no fetch is needed; only the full
  // report body of a non-newest attempt is unavailable (the documented gap).
  const _tailEntry = reforecastEntries.length
    ? reforecastEntries[reforecastEntries.length - 1]
    : null;
  const currentResolutionEntry = viewMomentIso
    ? viewEntry?.isResolution
      ? viewEntry
      : null
    : _tailEntry?.isResolution
      ? _tailEntry
      : null;
  const scrubIsResolution = !!currentResolutionEntry;
  // Symmetric to scrubIsResolution: is the CURRENT position the live forecast
  // sentinel (the latest FORECAST)? Keyed off the entry's isLive flag, so it's
  // true even though the sentinel is no longer the tail once resolution events
  // follow it — the modal uses this to label that position "LIVE" rather than
  // "couldn't load" (the sentinel carries no rewound snapshot).
  const scrubIsLive = isLiveScrubPosition(reforecastEntries, viewMomentIso);
  // Snapshot for the modal's resolution slide. `null` is meaningful: it tells the
  // modal to fall through to the pointer-fetched full resolution. See
  // lib/report/resolutionSlide.js for the three cases and why a bodyless snapshot
  // must not count as one.
  const _isTailResolution = currentResolutionEntry && currentResolutionEntry === _tailEntry;
  const scrubResolutionSnapshot = selectResolutionSnapshot(
    currentResolutionEntry,
    _isTailResolution,
  );
  // Render-only overlay of the snapshot onto `active`. Feeds the contract drawer,
  // the report open path, and the prob-line guard; the timeline lanes are
  // overlaid separately inside the laneRuns memo. Logic/async paths (submit,
  // reforecast, resolve, persistence) keep using the live `active`.
  // report / serverExplanation / serverContext are taken straight from the
  // snapshot (which always carries these keys, stored as `report: active.report ||
  // null` etc.) — a stored null is meaningful ("none at that vintage") and must
  // NOT fall back to the live value, or rewind would show modern report/context
  // over a snapshot that had none.
  const displayActive = viewSnap
    ? {
        ...active,
        contract: viewSnap.contract
          ? withLiveIdentity(viewSnap.contract, active.contract)
          : active.contract,
        events: viewSnap.events ?? active.events,
        report: viewSnap.report,
        serverExplanation: viewSnap.serverExplanation,
        serverContext: viewSnap.serverContext,
        serverPrediction: viewSnap.contract?.probability ?? active.serverPrediction,
      }
    : active;
  // The header compares the version it shows, latest or past, with the one before it in the
  // version list, which holds scheduled updates too (`active.history` holds only manual ones).
  const viewPrevVersion = usePreviousVersion({
    entries: reforecastEntries.length ? reforecastEntries : null,
    momentIso: viewSnap ? viewMomentIso : null,
  });
  // Where the probability-over-time line stops when rewound: the viewed mark's
  // own moment (generatedAt === the yellow guide line), so the line shows the
  // past trajectory up to that date plus the step to the viewed probability, and
  // nothing after. null = live (draws through today).
  const snapTruncateMs = viewSnap ? new Date(viewMomentIso).getTime() : null;
  return {
    reforecastEntries,
    viewMomentIso,
    viewEntry,
    viewSnap,
    scrubLoading,
    scrubToMoment,
    returnToLive,
    currentResolutionEntry,
    scrubIsResolution,
    scrubIsLive,
    scrubResolutionSnapshot,
    displayActive,
    viewPrevVersion,
    snapTruncateMs,
  };
}
